'use client';

import DefaultIcon from '@/assets/images/Andritz.png';
import { useProductsSimple } from '@/domain/products/hooks';
import { removeFromCart } from '@/features/cart/store/cart';
import { type CartProduct } from '@/features/cart/types';
import { useSolutionStore } from '@/features/products/store/solution';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { confirmPopup } from 'primereact/confirmpopup';
import { type MouseEvent, Suspense, useMemo, useState } from 'react';

const ProductDetailDialogWrapper = dynamic(
  () => import('@/features/products/components/ProductDetailDialog/ProductDetailDialogWrapper'),
);

type CartItemCardProps = {
  cartProduct: CartProduct;
};

const CartItemCard = ({ cartProduct }: CartItemCardProps) => {
  const showType = useSolutionStore((s) => s.showType);
  const { data: products } = useProductsSimple(showType);
  const [visible, setVisible] = useState(false);
  const { eCommerce } = usePiwikPro();

  const removeProduct = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // avoid modal to open
    confirmPopup({
      target: e.currentTarget,
      message: 'Remove solution?',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        eCommerce.ecommerceRemoveFromCart([
          {
            sku: `${cartProduct.idProduct}`,
          },
        ]);
        removeFromCart(cartProduct.idProduct);
      },
    });
  };

  const openModal = () => {
    setVisible(true);
  };

  const product = useMemo(
    () => products?.find((p) => p.idProduct === cartProduct.idProduct),
    [cartProduct.idProduct, products],
  );

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          p: '0.5rem',
          width: 'calc(100% - 18px)',
          '&:not(:last-child)': {
            pb: '10px',
            '&::after': {
              content: '""',
              position: 'absolute',
              margin: '0 auto',
              left: 0,
              right: 0,
              bottom: 0,
              width: '15%',
              borderBottom: '1px solid black',
            },
          },
        }}
      >
        <Box sx={{ margin: '10px', mr: '1rem' }}>
          <Image
            src={
              cartProduct.image2
                ? `${cartProduct.image2?.replace('Image', 'image')?.replace('Icons', 'icons')}`
                : DefaultIcon
            }
            width={50}
            height={50}
            alt={cartProduct.product}
          />
        </Box>
        <Box sx={{ flex: '1 1 0' }}>
          {product ? (
            <Button variant="text" onClick={openModal} sx={{ margin: '5px 0', padding: 0, textAlign: 'left' }}>
              {cartProduct.product}
            </Button>
          ) : (
            cartProduct.product
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: '2px' }}>
            <Box component="i" className="pi pi-tag" sx={{ verticalAlign: 'middle', mr: '0.5rem' }} />
            <Typography variant="body2" sx={{ verticalAlign: 'middle', lineHeight: 1 }}>
              {cartProduct.group}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Button onClick={removeProduct} startIcon={<CloseOutlined />} color="error" variant="outlined">
            Remove
          </Button>
        </Box>
      </Box>
      {visible && product && (
        <Suspense>
          <ProductDetailDialogWrapper
            idProduct={product.idProduct}
            onClose={() => {
              setVisible(false);
            }}
          />
        </Suspense>
      )}
    </>
  );
};

export default CartItemCard;
